/**
 * CollectionNotice — the APP 5 short‑form privacy notice, shown at each point where personal
 * information is collected: account sign‑up, the first screen of the client profile (the first
 * thing a client invited by a firm sees) and partner onboarding.
 *
 * One line the reader cannot miss, and the full notice and the Privacy Policy each one click
 * away in the same reader the acceptance checkboxes use. Self‑contained so the three call sites
 * do not have to carry modal state of their own.
 */
import React, { useState } from 'react';
import { Info } from 'lucide-react';
import LegalDocModal, { type LegalDoc } from './LegalDocModal.tsx';
import { COLLECTION_NOTICE } from '../../constants/legal/collectionNotice.ts';
import { PRIVACY_POLICY } from '../../constants/legal/index.ts';

export type CollectionNoticeVariant = 'signup' | 'profile' | 'partner';

const LEAD: Record<CollectionNoticeVariant, string> = {
  signup:
    'We collect the details you enter to run your account and build your plan. They are stored in Australia and never sold.',
  profile:
    'The details on the following screens are used only to model your options and produce your plan. Health details are optional and are used only with your consent. If a firm or family member invited you, they may have entered some details already — please check them.',
  partner:
    'We collect your name, work contact details and your firm’s details to run your firm’s workspace and billing.',
};

interface CollectionNoticeProps {
  variant: CollectionNoticeVariant;
  className?: string;
}

const linkClass =
  'font-semibold text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-700';

const CollectionNotice = ({ variant, className = '' }: CollectionNoticeProps) => {
  const [viewDoc, setViewDoc] = useState<LegalDoc | null>(null);
  return (
    <>
      <div
        role="note"
        data-testid={`collection-notice-${variant}`}
        className={`flex items-start gap-2.5 rounded-xl border border-blue-100 bg-blue-50/60 px-3.5 py-3 text-xs leading-relaxed text-gray-700 ${className}`}
      >
        <Info size={15} className="mt-0.5 shrink-0 text-blue-500" aria-hidden="true" />
        <span>
          {LEAD[variant]}{' '}
          <button type="button" onClick={() => setViewDoc(COLLECTION_NOTICE)} className={linkClass}>
            How we handle your information
          </button>
          {' · '}
          <button type="button" onClick={() => setViewDoc(PRIVACY_POLICY)} className={linkClass}>
            Privacy Policy
          </button>
        </span>
      </div>
      {viewDoc && <LegalDocModal doc={viewDoc} onClose={() => setViewDoc(null)} />}
    </>
  );
};

export default CollectionNotice;
